import React from 'react';
import { Typography } from '@/components/atoms/Typography';
import { TbArrowsRight, TbCheck, TbExternalLink, TbUser, TbX } from 'react-icons/tb';
import { DefaultHTMLSwornDeclaration } from '../DefaultHTMLSwornDeclaration';
import { ClaimResponse } from '@/api/lib/claim';
import { RestaurantResponse } from '@/api/lib/restaurant';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Spinner } from '@/components/atoms/Spinner';

interface SeeRestaurantClaimingProps {
  claimData?: ClaimResponse;
  restaurantData?: RestaurantResponse;
  isLoading?: boolean;
}

export const SeeRestaurantClaiming = ({ claimData, restaurantData, isLoading }: SeeRestaurantClaimingProps) => {
  return (
    <div className="divide-y xl:divide-y-0 divide-gray-200 xl:divide-x xl:grid grid-cols-10 w-full">
      <div className="flex flex-col pb-5 xl:pr-10 col-span-5">
        <div className="px-4 sm:px-0">
          <h3 className="text-lg font-semibold leading-7 text-gray-900">Informations sur le restaurant et les utilisateurs</h3>
        </div>
        <div className="bg-emerald-400 h-fit mt-6 p-5 rounded-xl items-center flex flex-col w-full">
          <Typography color="white" variant="h2" weight="bold">
            Demande de transfert du propriétaire
          </Typography>
          <div className="flex md:flex-row flex-col mt-2 text-white items-center">
            <div>
              <span className="flex gap-1 items-center font-semibold">
                <TbUser size={23} />
                {restaurantData?.emailOwner}
              </span>
              <p className="text-xs">Propriétaire actuel</p>
            </div>

            <TbArrowsRight className="my-2 mx-0 md:my-0 md:mx-5 rotate-90 md:rotate-0" size={24} />

            <div>
              <span className="flex gap-1 items-center font-semibold">
                <TbUser size={23} />
                {claimData?.email}
              </span>
              <p className="text-xs">Propriétaire futur</p>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <dl className="grid grid-cols-1 sm:grid-cols-2">
            <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Restaurant concerné :</dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:mt-2">{restaurantData?.name}</dd>
            </div>
            <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
              <dd className="text-sm leading-6 text-gray-700 sm:mt-8">{restaurantData?.formattedAddress}</dd>
            </div>

            <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Propriétaire actuel :</dt>
              <dd className="mt-1 flex items-center gap-2 text-sm leading-6 text-gray-700 sm:mt-2">
                <TbUser size={18} />
                {restaurantData?.firstNameOwner} {restaurantData?.lastNameOwner}
              </dd>
            </div>
            <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
              <dd className="text-sm leading-6 text-gray-700 md:gap-4 grid grid-cols-2 sm:mt-8">
                <span>{restaurantData?.phoneNumber}</span> <span>{restaurantData?.emailOwner}</span>
              </dd>
            </div>

            <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Propriétaire futur :</dt>
              <dd className="mt-1 flex items-center gap-2 text-sm leading-6 text-gray-700 sm:mt-2">
                <TbUser size={18} />
                {claimData?.firstName} {claimData?.lastName}
              </dd>
            </div>
            <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
              <dd className="text-sm leading-6 text-gray-700 md:gap-4 grid grid-cols-2 sm:mt-8">
                <span>{claimData?.phoneNumber}</span> <span>{claimData?.email}</span>
              </dd>
            </div>
            <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Societe.com actuel du resto/entreprise :</dt>
              <dd className="mt-1 flex items-center gap-2 text-sm leading-6 text-gray-700 sm:mt-2">
                <a
                  target="_blank"
                  href={`https://www.societe.com/cgi-bin/search?champs=${restaurantData?.name}`}
                  className="hover:underline flex items-center gap-1"
                >
                  <TbExternalLink size={18} />
                  {restaurantData?.name}
                </a>
              </dd>
            </div>
            <div className="border-t border-gray-100 px-4 py-6 sm:col-span-1 sm:px-0">
              <dd className="text-sm leading-6 text-gray-700 md:gap-4 grid grid-cols-2 sm:mt-8">
                <a
                  target="_blank"
                  href={`https://www.societe.com/cgi-bin/search?champs=${claimData?.company}`}
                  className="hover:underline flex items-center gap-1"
                >
                  <TbExternalLink size={18} />
                  {claimData?.company}
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <section className="col-span-5 xl:pl-10 space-y-10 divide-y">
        <div>
          <p className="text-lg text-center font-semibold text-slate-700">Déclaration sur l'honneur du futur propriétaire</p>
          <div className="flex gap-5 flex-wrap mt-6 text-black text-center">
            {claimData ? (
              <DefaultHTMLSwornDeclaration
                firstName={claimData?.firstName || ''}
                lastName={claimData?.lastName || ''}
                restaurantName={restaurantData?.name || ''}
                address={restaurantData?.formattedAddress || ''}
                date={claimData?.creationDate || new Date().toISOString()}
                suppliers={claimData?.suppliers || []}
                company={claimData?.company || ''}
              />
            ) : (
              <div className="border p-4 rounded-xl w-full">
                <p className="text-lg">Aucun certificat digital disponible</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
